<template>
  <div>
    <h4 class="mt-4">Custom Fields</h4>
    <div v-if="!projectsLoading">
      <b-row v-if="attributes.length > 0">
        <b-col cols="3">
          <strong>Field name</strong>
        </b-col>
        <b-col cols="3">
          <strong>Project name</strong>
        </b-col>
      </b-row>
      <b-row v-for="attribute in activeAttributes" :key="attribute.attribute_id" class="mb-1 mt-1">
        <b-col cols="3">
          {{attribute.name}}
        </b-col>
        <b-col cols="3">
          {{projectLookup[attribute.atlassian_project_id].name}}
        </b-col>
        <b-col cols="3">
          <b-button variant="danger" size="sm" @click="deleteAttribute(attribute.attribute_id)">Delete field</b-button>
        </b-col>
      </b-row>
      <b-row class="mt-2 mb-2">
        <b-col cols="3">
          <b-button variant="primary" @click="enableNewAttributeForm" v-if="!showNewAttributeForm">New field</b-button>
        </b-col>
      </b-row>
      <div class="form-group mt-2 mb-2" v-if="showNewAttributeForm">
        <b-row>
          <b-col cols="3">
            <b-form-input type="text"
                          id="attribute-name"
                          placeholder="Attribute name"
                          v-model="newAttributeName">
            </b-form-input>
          </b-col>
          <b-col cols="3">
            <b-form-select v-model="newAttributeProject"
                           placeholder="Select project"
                           :options="projectsArray">
            </b-form-select>
          </b-col>
          <b-col cols="3">
            <b-form-select v-model="newAttributeType"
                           :options="typeOptions">
            </b-form-select>
          </b-col>
          <b-col cols="3">
            <b-button-group>
              <b-button variant="primary"
                        :disabled="shouldDisableSubmitButton"
                        @click="createAttribute">Submit</b-button>
              <b-button @click="disableNewAttributeForm">Cancel</b-button>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row v-if="newAttributeType === 'select'">
          <b-col cols="3">
            <b-input-group>
              <b-form-input
                v-model="attributeOptionToAdd"
                placeholder="Add up to 10 options"
                aria-describedby="input-live-feedback"
                :state="nameState"
              ></b-form-input>
              <b-form-invalid-feedback id="input-live-feedback">
                Enter no more than 75 characters
              </b-form-invalid-feedback>
              <b-button :disabled="shouldDisableAddButton" @click="addAttributeOption" variant="info">Add</b-button>
            </b-input-group>
          </b-col>
          <b-col cols="9">
            <b-badge pill v-for="opt in newAttributeOptions" variant="primary">{{opt}}</b-badge>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      attributeOptionToAdd: '',
      attributes: [],
      newAttributeLoading: false,
      newAttributeName: null,
      newAttributeOptions: [],
      newAttributeProject: null,
      newAttributeType: 'string',
      projectLookup: {},
      projectsArray: [],
      projectsLoading: true,
      showNewAttributeForm: false,
      typeOptions: [
        {value: 'string', text: 'Standard field type'},
        {value: 'select', text: 'Field with pre-defined options'}
      ]
    }
  },
  computed: {
    shouldDisableSubmitButton () {
      if (!this.newAttributeName) {
        return true
      }
      if (!this.newAttributeProject) {
        return true
      }
      if (this.newAttributeType === 'select') {
        if (this.newAttributeOptions.length < 1 || this.newAttributeOptions.length > 10) {
          return true
        }
      }
      return false
    },
    shouldDisableAddButton () {
      if (this.newAttributeOptions.length >= 10) {
        return true
      }
      return this.attributeOptionToAdd.length <= 0 || this.attributeOptionToAdd.length > 75
    },
    nameState () {
      return this.attributeOptionToAdd.length <= 75
    },
    activeAttributes () {
      return this.attributes.filter(attribute => {
        return this.projectLookup.hasOwnProperty(attribute.atlassian_project_id)
      })
    }
  },
  methods: {
    createAttribute () {
      this.newAttributeLoading = true
      const payload = {
        name: this.newAttributeName,
        project: this.newAttributeProject,
        type: this.newAttributeType
      }
      if (this.newAttributeType === 'select') {
        payload['options'] = this.newAttributeOptions
      }
      this.$http.post(
        '/api/attribute',
        payload,
      ).then(response => {
        window.AP.flag.create({
          title: 'Success!',
          body: 'Custom field created successfully',
          type: 'success',
          close: 'auto'
        })
        this.newAttributeLoading = false
        this.getAttributes()
        this.disableNewAttributeForm()
      }, error => {
        this.newAttributeLoading = false
        let errorString = this.createErrorStringFromResponse(error.response.data)
        window.AP.flag.create({
          title: 'Error',
          body: `There was a problem saving your custom field: ${errorString}`,
          type: 'error'
        })
      })
    },
    deleteAttribute (attributeId) {
      if (!confirm('Are you sure you want to delete this field and all data associated with it?')) {
        return
      }
      this.newAttributeLoading = true
      this.$http.delete(
        `/api/attribute/${attributeId}`
      ).then(response => {
        window.AP.flag.create({
          title: 'Success!',
          body: 'Custom field deleted successfully',
          type: 'success',
          close: 'auto'
        })
        this.newAttributeLoading = false
        this.getAttributes()
      }, error => {
        this.newAttributeLoading = false
        let errorString = this.createErrorStringFromResponse(error.response.data)
        window.AP.flag.create({
          title: 'Error',
          body: `There was a problem deleting your custom field: ${errorString}`,
          type: 'error'
        })
      })
    },
    getAttributes () {
      this.$http.get(
        '/api/attribute'
      ).then(response => {
        this.attributes = response.data.attributes
      }, error => {
        let errorString = this.createErrorStringFromResponse(error.response.data)
        window.AP.flag.create({
          title: 'Error',
          body: `There was a problem retrieving your custom fields: ${errorString}`,
          type: 'error'
        })
      })
    },
    enableNewAttributeForm () {
      this.showNewAttributeForm = true
    },
    disableNewAttributeForm () {
      this.showNewAttributeForm = false
      this.newAttributeName = null
      this.newAttributeProject = null
      this.newAttributeOptions = []
      this.newAttributeType = 'string'
    },
    getProjects (startAt = 0, maxResults = 50) {
      window.AP.request({
        url: `/rest/api/3/project/search?startAt=${startAt}&maxResults=${maxResults}`,
        success: response => {
          // convert the string response to JSON
          let parsedResponse = JSON.parse(response)
          parsedResponse.values.forEach(i => {
            this.$set(this.projectLookup, i.id, i)
            this.projectsArray.push({'text': i.name, 'value': i})
          })
          if (parsedResponse.isLast === true) {
            // Add the instructions to the beginning of the array using unshift
            this.projectsArray.unshift({text: 'Select a project', value: null, disabled: true})
            this.projectsLoading = false
          } else {
            this.getProjects(startAt + maxResults, maxResults)
          }
        },
        error: () => {
          console.log(arguments)
          window.AP.flag.create({
            title: 'Error',
            body: `There was a problem retrieving your project data from Atlassian`,
            type: 'error'
          })
        }
      })
    },
    addAttributeOption () {
      this.newAttributeOptions.push(this.attributeOptionToAdd)
      this.attributeOptionToAdd = ''
    }
  },
  mounted () {
    this.getAttributes()
    this.getProjects()
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// CustomAttributesView.vue?c12eaa7e